import React, { useState } from 'react';
import {
  Check,
  Download,
  Share2,
  Clock,
  User,
  FileText,
  X,
} from 'lucide-react';

/**
 * SignatureSuccessModal Component
 *
 * Displays confirmation after successful agreement signing.
 * Shows signed details and next steps.
 *
 * Props:
 * - agreementId: string - Signed agreement ID
 * - signedAt: string - ISO timestamp when signed
 * - userName: string - Who signed
 * - userRole: string - "tenant" or "landlord"
 * - agreementDetails: object - Agreement info {rental_id, monthly_rent, etc}
 * - signatureUrl: string - Cloudinary URL of signature
 * - onClose: function - Called when modal closes
 * - onDownloadPDF: function - Called to download signed PDF
 */
const SignatureSuccessModal = ({
  agreementId = null,
  signedAt = null,
  userName = '',
  userRole = 'tenant',
  agreementDetails = {},
  signatureUrl = null,
  onClose = null,
  onDownloadPDF = null,
}) => {
  const [shareUrl, setShareUrl] = useState(null);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Copy share link
  const handleCopyLink = async () => {
    if (shareUrl) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopiedToClipboard(true);
        setTimeout(() => setCopiedToClipboard(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // Generate share URL (placeholder - would be implemented with actual sharing)
  const handleShareAgreement = () => {
    // In production, this would generate a shareable link
    setShareUrl(
      `${window.location.origin}/agreements/${agreementId}/view`
    );
  };

  const rental = agreementDetails.rental_id || {};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <div className="absolute top-4 right-4">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200 p-6 text-center">
          <div className="flex justify-center mb-3">
            <div className="bg-green-600 rounded-full p-3">
              <Check className="w-6 h-6 text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-green-900">Agreement Signed!</h2>
          <p className="text-green-700 text-sm mt-2">
            Your signature has been securely recorded
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Success Details */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">Signed On</p>
                <p className="text-sm text-gray-600">
                  {formatDate(signedAt)} at {formatTime(signedAt)}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">Signed By</p>
                <p className="text-sm text-gray-600">
                  {userName} ({userRole === 'tenant' ? 'Tenant' : 'Landlord'})
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">Agreement ID</p>
                <p className="text-sm font-mono text-gray-600 break-all">{agreementId}</p>
              </div>
            </div>
          </div>

          {/* Agreement Summary */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Agreement Summary
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              {rental.landlord_id?.name && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Landlord:</span>
                  <span className="font-medium text-gray-900">
                    {rental.landlord_id.name}
                  </span>
                </div>
              )}
              {rental.tenant_id?.name && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tenant:</span>
                  <span className="font-medium text-gray-900">
                    {rental.tenant_id.name}
                  </span>
                </div>
              )}
              {agreementDetails.monthly_rent && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Monthly Rent:</span>
                  <span className="font-medium text-gray-900">
                    ₹{agreementDetails.monthly_rent}
                  </span>
                </div>
              )}
              {agreementDetails.lease_duration_months && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-medium text-gray-900">
                    {agreementDetails.lease_duration_months} months
                  </span>
                </div>
              )}
              {rental.property?.city && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  <span className="font-medium text-gray-900">
                    {rental.property.city}, {rental.property.state}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Signature Preview */}
          {signatureUrl && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Your Signature
              </h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-center min-h-24">
                <img
                  src={signatureUrl}
                  alt="Signed signature"
                  className="max-w-full max-h-24 object-contain"
                />
              </div>
            </div>
          )}

          {/* What Happens Next */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              What Happens Next?
            </h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex gap-3">
                <div className="text-blue-600 font-bold flex-shrink-0">1</div>
                <p>
                  A confirmation email will be sent to all parties with the signed agreement.
                </p>
              </div>
              <div className="flex gap-3">
                <div className="text-blue-600 font-bold flex-shrink-0">2</div>
                <p>
                  {userRole === 'tenant'
                    ? 'The landlord will review and sign the agreement.'
                    : 'The tenant will be notified to review and sign the agreement.'}
                </p>
              </div>
              <div className="flex gap-3">
                <div className="text-blue-600 font-bold flex-shrink-0">3</div>
                <p>
                  Once both parties sign, the agreement will be fully executed and ready for your rental.
                </p>
              </div>
            </div>
          </div>

          {/* Share Link Section */}
          {shareUrl && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Share Agreement Link
              </h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono text-gray-600"
                />
                <button
                  onClick={handleCopyLink}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                    copiedToClipboard
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {copiedToClipboard ? '✓ Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t border-gray-200 bg-gray-50 p-6 space-y-3">
          {onDownloadPDF && (
            <button
              onClick={onDownloadPDF}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition"
            >
              <Download className="w-4 h-4" />
              Download Signed PDF
            </button>
          )}

          {!shareUrl && (
            <button
              onClick={handleShareAgreement}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition"
            >
              <Share2 className="w-4 h-4" />
              Generate Share Link
            </button>
          )}

          {onClose && (
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition"
            >
              Close
            </button>
          )}
        </div>

        {/* Footer Info */}
        <div className="bg-blue-50 border-t border-blue-200 p-4 text-xs text-blue-800 text-center">
          <p>
            A copy of your signed agreement is available in your Orello dashboard.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignatureSuccessModal;
